import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, FormControl, Menu, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography, useTheme } from "@mui/material";
import { ChevronLeftIcon, ChevronRightIcon, EllipsisVerticalIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { CiSearch } from "react-icons/ci";
import api from "./components/login/interceptors";
import { BASE_URL } from "./Constants";
import { PaginationButtons, RowsPerPageSelector } from "./utils/paginationUtils";
import ScheduleInterviewModal from "./ScheduleInterviewPage";
import usePlanGate from "./hooks/usePlanGate";
import MeetingIntegrations from "./components/modals/MeetingIntegrations";
import { FEEDBACK_ALLOWED_STATUSES, describeCalendarError, fetchInterviewFeedback } from "./utils/interviewApi";
import GoogleCalendarStep from "./components/modals/GoogleCalendarStep";
import AIInterviewStatusCell from "./components/aiInterview/AIInterviewStatusCell";
import { AIModeCaption, FeedbackSummaryCell, InterviewTime, PanelistNames, StatusHighlight } from "./components/interviews/InterviewCells";
import InterviewFeedbackModal from "./components/interviews/InterviewFeedbackModal";
import AIInterviewDetailsModal from "./components/aiInterview/AIInterviewDetailsModal";
import { aiSummaryOf } from "./utils/aiInterviewApi";
import useAISessions from "./hooks/useAISessions";
import AssignedInterviewDetailsModal, { ASSIGNED_VIEW } from "./components/interviews/AssignedInterviewDetailsModal";
import { can, readSession } from "./utils/organisationApi";
import { panelistDisplayName } from "./utils/panelists";

const canCompleteInterview = (interview) => {
  if (!interview.end_utc) return false;
  const end = new Date(interview.end_utc);
  return !Number.isNaN(end.getTime()) && end.getTime() <= Date.now();
};

const statusRank = {
  scheduled: 0,
  rescheduled: 1,
  pending: 2,
  completed: 3,
  cancelled: 4,
};

const startTimeValue = (interview) => {
  if (!interview.start_utc) return Number.POSITIVE_INFINITY;
  const time = new Date(interview.start_utc).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

const solidActionSx = ({ fill, hoverFill }) => ({
  textTransform: "none",
  minWidth: "max-content",
  height: "26px",
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: 1.2,
  px: 1.25,
  py: 0.25,
  borderRadius: "4px",
  backgroundColor: fill,
  color: "#ffffff",
  "&:hover": { backgroundColor: hoverFill },
  "&:focus-visible": { outline: "2px solid #000000", outlineOffset: "2px" },
});

const blueActionSx = solidActionSx({ fill: "#2252A5", hoverFill: "#1D4690" });

const greenActionSx = solidActionSx({ fill: "#166534", hoverFill: "#14532D" });

// One button for the next step; everything else lives in the ⋯ menu
const InterviewActions = ({ interview, onSchedule, onAction }) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isPending = interview.status === "pending";
  const isActive = interview.status === "scheduled" || interview.status === "rescheduled";
  if (!isPending && !isActive) return null;

  const canComplete = canCompleteInterview(interview);
  const candidateName = interview.candidate?.name || "this candidate";
  const closeMenu = () => setMenuAnchor(null);
  const runFromMenu = (callback) => () => {
    closeMenu();
    callback();
  };

  let primary;
  if (isPending) {
    primary = { label: "Schedule", sx: blueActionSx, onClick: () => onSchedule(interview) };
  } else if (canComplete) {
    primary = { label: "Complete", sx: greenActionSx, onClick: () => onAction(interview, "complete") };
  } else {
    primary = { label: "Reschedule", sx: blueActionSx, onClick: () => onSchedule(interview) };
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button size="small" onClick={primary.onClick} sx={primary.sx}>
        {primary.label}
      </Button>
      <button
        type="button"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        aria-label={`More actions for ${candidateName}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(menuAnchor)}
        className="inline-flex h-[26px] w-[26px] items-center justify-center rounded border bg-white hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
        style={{ borderColor: theme.palette.secondary.v4, color: theme.palette.text.icon }}
      >
        <EllipsisVerticalIcon className="h-4 w-4" />
      </button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 200,
              border: `1px solid ${theme.palette.secondary.main}`,
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.14)",
            },
          },
        }}
        sx={{ "& .MuiMenuItem-root": { fontFamily: "Instrument Sans, sans-serif", fontSize: "0.875rem" } }}
      >
        {isActive && canComplete && (
          <MenuItem onClick={runFromMenu(() => onSchedule(interview))}>Reschedule</MenuItem>
        )}
        {isActive && !canComplete && (
          <MenuItem disabled sx={{ flexDirection: "column", alignItems: "flex-start" }}>
            Mark complete
            <span className="text-xs">Available after the interview end time</span>
          </MenuItem>
        )}
        <MenuItem onClick={runFromMenu(() => onAction(interview, "cancel"))} sx={{ color: "#b91c1c" }}>
          Cancel interview
        </MenuItem>
      </Menu>
    </div>
  );
};

// Panelists can't schedule or cancel (spec 3.2); they open the candidate and job they're interviewing for
const PanelistInterviewActions = ({ interview, onView }) => {
  const candidateName = interview.candidate?.name || "the candidate";
  return (
    <div className="flex items-center gap-1.5">
      <Button size="small" onClick={() => onView(interview, ASSIGNED_VIEW.CANDIDATE)} sx={blueActionSx} aria-label={`View ${candidateName}`}>
        Candidate
      </Button>
      <Button
        size="small"
        onClick={() => onView(interview, ASSIGNED_VIEW.JOB)}
        sx={blueActionSx}
        aria-label={`View job description for ${interview.jd_title || "this interview"}`}
      >
        Job description
      </Button>
    </div>
  );
};

const InterviewsPage = () => {
  const theme = useTheme();
  const isPanelist = !can(readSession(), "hiring");
  const [assignedView, setAssignedView] = useState(null);
  const firstHeaderCellRef = useRef(null);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  // Booking or rescheduling an interview needs an active plan
  const scheduleGate = usePlanGate();
  const [editingInterview, setEditingInterview] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionInterview, setActionInterview] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionText, setActionText] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSaving, setActionSaving] = useState(false);
  const [aiDetailsInterview, setAiDetailsInterview] = useState(null);
  const [feedbackInterview, setFeedbackInterview] = useState(null);
  // interview id → server feedback summary from GET /interviews/{id}/feedback (null when none)
  const [feedbackSummaries, setFeedbackSummaries] = useState({});
  const feedbackRequested = useRef(new Set());

  // SiftHire interview id → AISession from the AI interviewer service. Live ones refresh every 15 s,
  // except while the details modal is open (it polls that interview itself).
  const { sessions: aiSessions, reload: loadAISessions, update: updateAISession } = useAISessions({
    autoLoad: false,
    interviews: interviews.map((interview) => ({
      id: interview.interview_id,
      startTime: interview.start_utc,
      source: interview,
    })),
    paused: Boolean(aiDetailsInterview),
  });

  const loadInterviews = async () => {
    setIsLoading(true);
    setListError("");
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const pageSize = 100;
      const all = [];
      let page = 1;
      let count = 0;
      do {
        const params = { page, limit: pageSize, timezone, sort: "start_utc" };
        if (statusFilter) params.status = statusFilter;
        const response = await api.get(`${BASE_URL}/api/interviews`, { params });
        const data = response.data || {};
        const batch = Array.isArray(data.interviews) ? data.interviews : [];
        count = data.count ?? all.length + batch.length;
        all.push(...batch);
        page += 1;
        if (!batch.length) break;
      } while (all.length < count && page <= 50);
      setInterviews(all);
      loadAISessions();
    } catch (err) {
      console.error("Error fetching interviews:", err);
      setInterviews([]);
      setListError(err?.response?.data?.message || err?.response?.data?.error || "Could not load interviews.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInterviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);
  

  const filteredInterviews = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    const searched = search
      ? interviews.filter((interview) => {
        const panelists = (interview.panelists || []).map((person) => `${panelistDisplayName(person)} ${person.email || ""}`).join(" ");
        const candidate = interview.candidate || {};
        return (
          (candidate.name || "").toLowerCase().includes(search) ||
          (candidate.email || "").toLowerCase().includes(search) ||
          (interview.jd_title || "").toLowerCase().includes(search) ||
          (interview.round_name || "").toLowerCase().includes(search) ||
          (interview.status || "").toLowerCase().includes(search) ||
          (interview.candidate_response || "").toLowerCase().includes(search) ||
          panelists.toLowerCase().includes(search)
        );
      })
      : interviews;

    const valueOf = (interview) => {
      switch (sortField) {
        case "candidate":
          return interview.candidate?.name || "";
        case "panelists":
          return (interview.panelists || []).map((person) => panelistDisplayName(person)).filter(Boolean).join(", ");
        case "jd_title":
          return interview.jd_title || "";
        case "round_name":
          return interview.round_name || "";
        case "status":
          return interview.status || "";
        case "start_utc": {
          const time = interview.start_utc ? new Date(interview.start_utc).getTime() : 0;
          return Number.isNaN(time) ? 0 : time;
        }
        case "candidate_response":
          return interview.candidate_response || "";
        default:
          return "";
      }
    };

    return [...searched].sort((a, b) => {
      if (!sortField) {
        const rankA = statusRank[a.status] ?? 5;
        const rankB = statusRank[b.status] ?? 5;
        if (rankA !== rankB) return rankA - rankB;
        return startTimeValue(a) - startTimeValue(b);
      }
      const aValue = valueOf(a);
      const bValue = valueOf(b);
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }
      const aText = String(aValue).toLowerCase();
      const bText = String(bValue).toLowerCase();
      if (aText < bText) return sortOrder === "asc" ? -1 : 1;
      if (aText > bText) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [interviews, searchQuery, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredInterviews.length / rowsPerPage) || 1;
  const pageRows = filteredInterviews.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const loadFeedbackSummary = (interviewId) =>
    fetchInterviewFeedback(interviewId)
      .then(({ summary }) => setFeedbackSummaries((current) => ({ ...current, [interviewId]: summary })))
      .catch((err) => {
        console.error("Error fetching interview feedback:", err);
        setFeedbackSummaries((current) => ({ ...current, [interviewId]: null }));
      });

  // Feedback summaries for the interviews on this page (pending interviews can't have feedback)
  const pageFeedbackIds = pageRows
    .filter((interview) => interview.status !== "pending")
    .map((interview) => interview.interview_id)
    .filter(Boolean)
    .join(",");

  useEffect(() => {
    pageFeedbackIds.split(",").filter(Boolean).forEach((interviewId) => {
      if (feedbackRequested.current.has(interviewId)) return;
      feedbackRequested.current.add(interviewId);
      loadFeedbackSummary(interviewId);
    });
    // loadFeedbackSummary only sets state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFeedbackIds]);

  // After a save or delete, take the fresh totals from the server
  const handleFeedbackChange = (interviewId) => {
    loadFeedbackSummary(interviewId);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setTimeout(() => firstHeaderCellRef.current?.focus(), 0);
  };

  const submitAction = async () => {
    if (!actionInterview || !actionType) return;
    setActionSaving(true);
    setActionError("");
    try {
      if (actionType === "cancel") {
        await api.post(`${BASE_URL}/api/interviews/${actionInterview.interview_id}/cancel`, {
          reason: actionText.trim(),
          notify: true,
        });
      } else {
        await api.post(`${BASE_URL}/api/interviews/${actionInterview.interview_id}/complete`, {
          notes: actionText.trim(),
        });
      }
      setActionInterview(null);
      setActionType("");
      setActionText("");
      loadInterviews();
    } catch (err) {
      const described = describeCalendarError(err, "Could not update the interview.");
      setActionError(described.message);
    } finally {
      setActionSaving(false);
    }
  };

  const headerCellSx = {
    backgroundColor: theme.palette.secondary.v5,
    color: theme.palette.text.main,
    zIndex: 10,
  };

  return (
    <div
      className="flex flex-col w-full h-full"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <div
        className="w-full flex flex-wrap justify-between items-center gap-2 py-5 px-4 sm:px-6 max-sm:pl-12 border border-l-0 border-r-0 shadow-md"
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.main,
          borderColor: theme.palette.secondary.v4,
        }}
      >
        <h1 className="font-semibold text-2xl">Interviews</h1>
        <div className="flex flex-wrap items-center gap-2">
          <FormControl
            size="small"
            sx={{
              minWidth: 160,
              backgroundColor: theme.palette.primary.main,
              borderRadius: "4px",
              border: `1px solid ${theme.palette.secondary.main}`,
              "& .MuiInputBase-root": {
                height: "30px",
                paddingTop: "4px",
                paddingBottom: "4px",
                paddingLeft: "10px",
                paddingRight: "10px",
                boxSizing: "border-box",
                color: theme.palette.text.main,
                [theme.breakpoints.down("md")]: {
                  fontSize: "14px",
                },
              },
              "& .MuiSelect-select": {
                padding: 0,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiSelect-icon": {
                color: theme.palette.text.main,
              },
              "& fieldset": {
                border: "none",
              },
            }}
          >
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              onOpen={() => setStatusOpen(true)}
              onClose={() => setStatusOpen(false)}
              displayEmpty
              size="small"
              id="interview-status-filter"
              inputProps={{
                "aria-label": "Filter by status",
                "aria-describedby": "interview-status-filter-description",
                "aria-expanded": statusOpen,
              }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="rescheduled">Rescheduled</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
            <Typography id="interview-status-filter-description" className="sr-only">
              Opens a dropdown to filter interviews by status.
            </Typography>
          </FormControl>
          <div className="relative w-full sm:w-80">
            <CiSearch
              style={{
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.palette.text.main,
                pointerEvents: "none",
                fontSize: "16px",
              }}
            />
            <input
              type="text"
              placeholder="Search this page"
              aria-label="Search this page"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`border px-2 py-1 pl-8 text-sm rounded w-full focus:outline-none focus:ring-0 focus:border ${searchQuery ? "pr-8" : ""}`}
              style={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.text.main,
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-gray-medium hover:text-gray-700"
                aria-label="Clear search"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Meetings come from the HR person's calendar, so panelists don't connect one */}
          {!isPanelist && (
            <>
              <MeetingIntegrations />
              <Button
                {...scheduleGate.controlProps}
                onClick={scheduleGate.guard(() => {
                  setEditingInterview(null);
                  setIsScheduleOpen(true);
                })}
                sx={{
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.text.light,
                  textTransform: "none",
                  minWidth: "max-content",
                }}
              >
                Schedule interview
              </Button>
              {scheduleGate.hint}
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="mx-auto px-6 text-center mt-16">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
          <p>Loading interviews...</p>
        </div>
      ) : (
        <>
          <div
            className="overflow-auto border rounded-md mx-6 mt-4 mb-0 max-md:mx-4"
            style={{ color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}
          >
            {listError && (
              <div className="m-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{listError}</div>
            )}
            {listError ? null : filteredInterviews.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center h-80 text-accent-gray"
                style={{ background: theme.palette.primary.main }}
              >
                <p className="text-lg font-medium mb-1">No interviews scheduled</p>
                <p className="text-sm max-w-md text-center px-6">
                  Scheduled interviews will appear here.
                </p>
              </div>
            ) : (
              <Table
                stickyHeader
                className="w-full min-w-max"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "& .MuiTableCell-root": { paddingLeft: "15px", paddingRight: 0 },
                  "& .MuiTableCell-root:first-of-type": { paddingLeft: 3 },
                  "& .MuiTableCell-root:last-of-type": { paddingRight: "10px" },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell ref={firstHeaderCellRef} tabIndex={0} sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === "candidate"}
                        direction={sortField === "candidate" ? sortOrder : "asc"}
                        onClick={() => handleSort("candidate")}
                      >
                        Candidate
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === "panelists"}
                        direction={sortField === "panelists" ? sortOrder : "asc"}
                        onClick={() => handleSort("panelists")}
                      >
                        Panelists
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === "jd_title"}
                        direction={sortField === "jd_title" ? sortOrder : "asc"}
                        onClick={() => handleSort("jd_title")}
                      >
                        Job
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === "round_name"}
                        direction={sortField === "round_name" ? sortOrder : "asc"}
                        onClick={() => handleSort("round_name")}
                      >
                        Round
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === "status"}
                        direction={sortField === "status" ? sortOrder : "asc"}
                        onClick={() => handleSort("status")}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>Feedback</TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === "start_utc"}
                        direction={sortField === "start_utc" ? sortOrder : "asc"}
                        onClick={() => handleSort("start_utc")}
                      >
                        Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((interview) => {
                    const panelistNames = (interview.panelists || []).map((person) => panelistDisplayName(person)).filter(Boolean);
                    const aiSummary = aiSummaryOf(interview, aiSessions[interview.interview_id]);
                    return (
                    <TableRow key={interview.interview_id}>
                      <TableCell>
                        <Typography sx={{ fontSize: "0.875rem" }}>{interview.candidate?.name || "—"}</Typography>
                        <Typography sx={{ fontSize: "0.75rem", wordBreak: "break-all" }}>{interview.candidate?.email || ""}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: "200px" }}>
                        <PanelistNames names={panelistNames} aiMode={interview.ai_mode} />
                      </TableCell>
                      <TableCell sx={{ overflowWrap: "normal", wordBreak: "normal", maxWidth: "300px" }}>{interview.jd_title || "—"}</TableCell>
                      <TableCell sx={{ overflowWrap: "normal", wordBreak: "normal", maxWidth: "220px" }}>{interview.round_name || "—"}</TableCell>
                      <TableCell>
                        {/* The AI interviewer's status when AI is on, otherwise the interview status */}
                        {aiSummary?.enabled ? (
                          <AIInterviewStatusCell
                            interviewId={interview.interview_id}
                            summary={aiSummary}
                            onOpen={() => setAiDetailsInterview(interview)}
                          />
                        ) : (
                          <>
                            <StatusHighlight status={interview.status} />
                            <AIModeCaption aiMode={interview.ai_mode} />
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <FeedbackSummaryCell
                          summary={feedbackSummaries[interview.interview_id]}
                          loaded={interview.status === "pending" || interview.interview_id in feedbackSummaries}
                          canAdd={FEEDBACK_ALLOWED_STATUSES.includes(interview.status)}
                          onOpen={() => setFeedbackInterview(interview)}
                        />
                      </TableCell>
                      <TableCell><InterviewTime interview={interview} /></TableCell>
                      <TableCell>
                        {isPanelist ? (
                          <PanelistInterviewActions
                            interview={interview}
                            onView={(item, view) => setAssignedView({ interview: item, view })}
                          />
                        ) : (
                        <InterviewActions
                          interview={interview}
                          onSchedule={(item) =>
                            scheduleGate.guard(() => {
                              setEditingInterview(item);
                              setIsScheduleOpen(true);
                            })()
                          }
                          onAction={(item, type) => {
                            setActionInterview(item);
                            setActionType(type);
                            setActionText("");
                            setActionError("");
                          }}
                        />
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
          {filteredInterviews.length > 0 && (
            <div className="mt-auto w-full flex flex-col justify-center items-center px-3 py-3 sm:px-6">
              <div className="relative flex max-lg:flex-col w-full justify-center items-center">
                <RowsPerPageSelector
                  rowsPerPage={rowsPerPage}
                  handleChangeRowsPerPage={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setCurrentPage(1);
                  }}
                />
                <nav aria-label="Pagination" className="flex flex-wrap justify-center items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage > 1 ? currentPage - 1 : 1)}
                    className="relative inline-flex items-center border rounded px-2 py-2 bg-white hover:bg-accent-blue hover:text-white text-xs sm:text-sm"
                    style={{ borderColor: theme.palette.secondary.v4 }}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
                    <span className="hidden xs:inline ml-1">Prev</span>
                  </button>
                  <PaginationButtons
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                    theme={theme}
                  />
                  <button
                    onClick={() => handlePageChange(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    className="relative inline-flex items-center rounded px-2 py-2 bg-white hover:bg-accent-blue hover:text-white text-xs sm:text-sm border"
                    style={{ borderColor: theme.palette.secondary.v4 }}
                    disabled={currentPage === totalPages}
                  >
                    <span className="hidden xs:inline mr-1">Next</span>
                    <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
      <ScheduleInterviewModal
        isOpen={isScheduleOpen}
        interview={editingInterview}
        onClose={() => {
          setIsScheduleOpen(false);
          setEditingInterview(null);
        }}
        onScheduled={loadInterviews}
      />
      <AssignedInterviewDetailsModal
        interview={assignedView?.interview}
        view={assignedView?.view}
        onClose={() => setAssignedView(null)}
      />
      <InterviewFeedbackModal
        isOpen={Boolean(feedbackInterview)}
        interview={feedbackInterview}
        onClose={() => setFeedbackInterview(null)}
        onFeedbackChange={handleFeedbackChange}
        onOpenAIReport={() => {
          setAiDetailsInterview(feedbackInterview);
          setFeedbackInterview(null);
        }}
      />
      <AIInterviewDetailsModal
        isOpen={Boolean(aiDetailsInterview)}
        onClose={() => setAiDetailsInterview(null)}
        interviewId={aiDetailsInterview?.interview_id}
        title={aiDetailsInterview?.candidate?.name || ""}
        subtitle={[aiDetailsInterview?.jd_title, aiDetailsInterview?.round_name].filter(Boolean).join(" · ")}
        startTime={aiDetailsInterview?.start_utc}
        onSessionChange={updateAISession}
      />
      {actionInterview && (
        <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
          <div className="w-full max-w-lg my-auto mx-4 bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center py-4 px-5 border-b" style={{ borderColor: theme.palette.secondary.v4 }}>
              <h2 className="font-semibold">{actionType === "cancel" ? "Cancel interview" : "Complete interview"}</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setActionInterview(null)}
                className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4 flex flex-col gap-3">
              <label className="text-sm font-medium" htmlFor="interview-action-text">
                {actionType === "cancel" ? "Reason" : "Notes"}
              </label>
              <textarea
                id="interview-action-text"
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-md"
                style={{ borderColor: theme.palette.secondary.v4 }}
              />
              {actionError && <p className="text-sm text-red-600">{actionError}</p>}
            </div>
            <div className="px-5 py-3 flex justify-end border-t" style={{ borderColor: theme.palette.secondary.v4 }}>
              <Button
                onClick={submitAction}
                disabled={actionSaving}
                sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
              >
                {actionSaving ? "Saving..." : actionType === "cancel" ? "Cancel interview" : "Complete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewsPage;
